<template>
  <b-modal
    v-model="show"
    hide-header
    hide-footer
    centered
    @hide="hideModal"
  >
    <form
      id="editModal"
      @submit.prevent="editUser"
    >
      <div class="modal-header align-items-center">
        <h2
          id="ModalLabel"
          class="modal-title"
          v-text="userReadOnly ? 'Просмотр пользователя' : 'Редактирование пользователя'"
        ></h2>
        <i class="material-icons">edit</i>
      </div>
      <div class="modal-body">
        Логин пользователя:
        <span id="login_place">
          {{ userEdited.email }}
        </span>
        <hr>
        <div class="form-group">
          <label
            for="name"
            class="col-form-label"
          >
            Имя на портале:
          </label>
          <input
            id="name"
            v-model="userEdited.name"
            class="form-control"
            placeholder="Имя пользователя"
            :disabled="userReadOnly"
          >
        </div>
        <div class="form-group">
          <label
            for="phone"
            class="col-form-label"
          >
            Рабочий телефон:
          </label>
          <the-mask
            id="phone"
            v-model="userEdited.phone"
            :mask="['+### (##) ###-##-##', '+# (###) ###-##-##']"
            masked
            class="form-control"
            placeholder="+7 (921) 123-45-67 или +375 (92) 123-45-67"
            :disabled="userReadOnly"
          />
        </div>
        <div
          ref="roleSelection"
          class="form-group"
        >
          <label
            for="roles"
            class="col-form-label"
          >
            Роль:
          </label>
          <multiselect
            id="roles"
            :value="userRoles"
            :options="roleListFiltered"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            placeholder="Добавить роль"
            selected-label="Роль добавлена"
            select-label="Добавить роль"
            deselect-label="Убрать роль"
            :disabled="isUserSelf || userReadOnly"
            label="description"
            track-by="description"
            @select="addRole"
            @remove="removeRole"
            @search-change="disableTags"
            @close="disableTags"
          >
            <template slot="noResult">
              По данному запросу ничего не найдено
            </template>
            <template slot="noOptions">
              Нет доступных ролей
            </template>
          </multiselect>
        </div>
        <div
          v-if="hasDealerCode && (canSetAnyUR || canSetUR)"
          class="form-group"
        >
          <label
            for="URCodes"
            class="col-form-label"
          >
            Дилер:
          </label>
          <multiselect
            id="URCodes"
            v-model="dealersSelected"
            :options="dealerList"
            :multiple="true"
            :taggable="canSetAnyUR"
            :loading="isLoading"
            :placeholder="dealersSelectLabel"
            :disabled="isUserSelf || userReadOnly"
            tag-placeholder="Добавить новый UR-код"
            selected-label=""
            select-label="Выбрать"
            deselect-label="Отменить"
            label="URCode"
            track-by="URCode"
            @tag="addURCode"
          >
            <template slot="noResult">
              По данному запросу ничего не найдено
            </template>
            <template slot="noOptions">
              Нет доступных дилеров
            </template>
          </multiselect>
        </div>
        <div
          v-if="hasCarrierCode && user.interfaces.includes('UM-user-set-carrier')"
          class="form-group"
        >
          <label
            for="carrier-code"
            class="col-form-label"
          >
            Перевозчик:
          </label>
          <multiselect
            id="carrier-code"
            :disabled="userReadOnly"
            v-model="carrier.selected"
            :options="carrier.list"
            :options-limit="5"
            :show-no-results="false"
            :loading="carrier.loading"
            track-by="id"
            label="name"
            placeholder="-"
            @open="carrierOpen"
            @search-change="carrierSearch"
          >
            <template #singleLabel="{ option }">
              {{ option.name }}
            </template>
            <template #option="{ option }">
              {{ option.name }}
            </template>
          </multiselect>
        </div>
        <div
          v-if="hasClaimAgentCode && (user.interfaces.includes('UM-user-set-claim-agent') || userReadOnly)"
          class="form-group"
        >
          <label
            for="claim-agent-codes"
            class="col-form-label"
          >
            Претензионный агент:
          </label>
          <multiselect
            id="claim-agent-codes"
            v-model="claimAgent.selected"
            :options="claimAgent.list"
            :options-limit="5"
            :multiple="true"
            :show-no-results="false"
            :loading="claimAgent.loading"
            :disabled="userReadOnly"
            track-by="id"
            label="name"
            placeholder="-"
            @open="claimAgentOpen"
            @search-change="claimAgentSearch"
          >
            <template #singleLabel="{ option }">
              {{ option.name }}
            </template>
            <template #option="{ option }">
              {{ option.name }}
            </template>
          </multiselect>
        </div>
        <div
          v-if="userEdited.GSDBCode"
          class="form-group"
        >
          <label
            for="GSDB"
            class="col-form-label"
          >
            GSDB:
          </label>
          <input
            id="GSDB"
            v-model="userEdited.GSDBCode"
            class="form-control"
            readonly
          >
        </div>
        <template v-if="userEdited.canChangePassword && !userReadOnly">
          <div class="form-group">
            <label
              for="password"
              class="col-form-label"
            >
              Новый пароль:
            </label>
            <input
              id="password"
              v-model="password"
              class="form-control"
              type="password"
              placeholder="*********"
              autocomplete="new-password"
            >
            <b-form-invalid-feedback id="password-feedback">
              {{ validation.password.error }}
            </b-form-invalid-feedback>
          </div>
          <div class="form-group">
            <label
              for="newPasswordConfirm"
              class="col-form-label"
            >
              Подтвердите пароль:
            </label>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              class="form-control"
              type="password"
              placeholder="*********"
              autocomplete="new-password"
            >
            <b-form-invalid-feedback id="confirm-password-feedback">
              {{ validation.confirmPassword.error }}
            </b-form-invalid-feedback>
          </div>
          <password-rules></password-rules>
        </template>
      </div>
      <div class="modal-footer">
        <button
          class="btn btn-secondary-ford-gray modal-ext"
          type="button"
          @click="hideModal"
        >
          Отмена
        </button>
        <button
          class="btn btn-primary-ford-orange modal-ext"
          type="submit"
          v-if="!userReadOnly"
        >
          Изменить
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import PasswordRules from '@/components/users/PasswordRules';
import { mongoRegexI } from '@/utils/mongo-regex';

export default {
  name: 'EditUser',
  components: {
    PasswordRules,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      required: true,
    },
    userEdited: {
      type: Object,
      required: true,
    },
    roleList: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    show: false,
    userRoles: [],
    password: '',
    confirmPassword: '',
    dealerList: [],
    dealersSelected: [],
    validation: {
      password: { error: '', state: null },
      confirmPassword: { error: '', state: null },
    },
    isLoading: false,
    carrier: {
      selected: '',
      list: [],
      loading: false,
    },
    claimAgent: {
      selected: [],
      list: [],
      loading: false,
    },
  }),
  computed: {
    isUserSelf() {
      return this.userEdited.id === this.user.id;
    },
    canSetUR() {
      return this.user.interfaces.includes('UM-user-set-ur');
    },
    canSetAnyUR() {
      return this.user.interfaces.includes('UM-user-set-any-ur');
    },
    dealersSelectLabel() {
      return `Выберите URCode${this.canSetAnyUR ? ' или добавьте новый' : ''}`;
    },
    userReadOnly() {
      return this.$store.state.user.interfaces.includes('UM-SVO-readonly');
    },
    roleListFiltered() {
      if (!this.value) {
        return [];
      }

      const rolesAvailableForEditing = this.roleList
        .filter(role => {
          return role.availableFor.filter(roleAvailable => {
            return this.user.role.includes(roleAvailable);
          }).length;
        })
        .map(role => ({
          isAvailableForEditing: true,
          ...role,
        }));

      const rolesNotAvailableForEditing = this.roleList
        .filter(it => this.userEdited.role.includes(it.name))
        .filter(role => !role.availableFor.filter(roleAvailable => this.user.role.includes(roleAvailable)).length);

      const roleListFiltered = rolesAvailableForEditing.concat(rolesNotAvailableForEditing);

      return roleListFiltered;
    },
    userRolesFormatted() {
      return this.userRoles.map(it => it.name);
    },
    hasClaimAgentCode() {
      return this.userRolesFormatted.includes('claimAgentSVO');
    },
    hasCarrierCode() {
      return this.userRolesFormatted.includes('carrierSVO');
    },
    hasDealerCode() {
      return ['dealerSVO', 'dealerCB', 'dealerAdministratorCB', 'dealerAdministratorSVO']
        .some((role) => this.userRolesFormatted.includes(role));
    },
  },
  watch: {
    value(val) {
      this.show = val;
      this.userRoles = this.roleListFiltered.filter(it => {
        return this.userEdited.role.includes(it.name);
      });

      this.disableTags();

      this.dealersSelected = this.userEdited.URCodes.map(it => ({ URCode: it }));

      if (val && this.userEdited.carrier) {
        this.carrier.selected = { ...this.userEdited.carrier };
      } else {
        this.carrier.selected = '';
      }

      if (val && this.userEdited.claimAgents) {
        this.claimAgent.selected = this.userEdited.claimAgents.map((it) => ({ ...it }));
      } else {
        this.claimAgent.selected = [];
      }

      if (!val) {
        return;
      }

      if (this.canSetAnyUR) {
        this.getDealers();
        return;
      }

      if (this.canSetUR) {
        this.dealerList = this.user.URCodes.map(it => ({ URCode: it }));
      }
    },
  },
  methods: {
    hideModal() {
      Object.assign(this.$data, this.$options.data());
      this.$emit('input', false);
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Ошибка',
        variant: 'danger',
        solid: true,
        autoHideDelay: 15000,
      });
    },
    validatePhone() {
      const phone = this.userEdited.phone;
      if (!phone) return true;

      return /^(\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}|\+375 \(\d{2}\) \d{3}-\d{2}-\d{2})$/.test(phone);
    },
    async editUser() {
      if (!this.validatePhone()) {
        this.showErrorToast('Некорректный формат телефона. Телефон должен быть в формате +7 (XXX) XXX-XX-XX или +375 (XX) XXX-XX-XX.');
        return;
      }

      if (!this.validateForm()) {
        return;
      }

      const roleList = this.userRoles.map(it => it.name);
      const requestOptions = {
        name: this.userEdited.name,
        phone: this.userEdited.phone,
        role: roleList,
        URCodes: this.hasDealerCode ? this.dealersSelected.map(it => it.URCode) : [],
        CarrierCode: this.hasCarrierCode ? this.carrier.selected.code : undefined,
        ClaimAgentCodes: this.hasClaimAgentCode ? this.claimAgent.selected.map((it) => it.code) : [],
      };
      if (this.userEdited.canChangePassword) {
        requestOptions.password = this.password;
      }

      await this.$http.post(`users/1.0/users/${this.userEdited.id}`, requestOptions);

      Object.assign(this.$data, this.$options.data());
      this.$emit('editUser');
    },
    validateForm() {
      Object.assign(this.$data.validation, this.$options.data().validation);

      let isFormValid = true;

      if (this.password && this.password.length < 12) {
        this.validation.password.error = 'Длина пароля менее 12 символов';
        this.validation.password.state = false;
        isFormValid = false;
      }

      if (this.password && !this.confirmPassword) {
        this.validation.confirmPassword.error = 'Необходимо заполнить поле';
        this.validation.confirmPassword.state = false;
        isFormValid = false;
      }

      if (this.password !== this.confirmPassword) {
        this.validation.confirmPassword.error = 'Пароль и подтверждение не совпадают';
        this.validation.confirmPassword.state = false;
        isFormValid = false;
      }

      return isFormValid;
    },
    async getDealers() {
      this.isLoading = true;
      try {
        const { items } = await this.$http.get('dealers/1.0/dealers');
        this.dealerList = items;
      } finally {
        this.isLoading = false;
      }
    },
    addURCode(URCode) {
      this.dealersSelected.push({ URCode });
    },
    addRole(role) {
      this.userRoles.push(role);
      this.disableTags();
    },
    removeRole(role) {
      if (!role.isAvailableForEditing) {
        return;
      }

      this.userRoles = this.userRoles.filter(it => it.id !== role.id);
      this.disableTags();
    },
    disableTags() {
      setTimeout(() => {
        Array.from(this.$refs.roleSelection.querySelectorAll('.multiselect__tag'))
          .filter(tag => this.roleListFiltered.find(role => !role.isAvailableForEditing && tag.firstChild.innerText === role.description))
          .forEach(it => it.classList.add('multiselect__tag--disabled'));
        Array.from(this.$refs.roleSelection.querySelectorAll('.multiselect__option'))
          .filter(tag => this.roleListFiltered.find(role => !role.isAvailableForEditing && tag.firstChild.innerText === role.description))
          .forEach(it => it.classList.add('d-none'));
      }, 0);
    },
    carrierOpen() {
      if (!this.carrier.list.length) {
        this.carrierSearch();
      }
    },
    async carrierSearch(searchStr) {
      this.carrier.loading = true;

      try {
        const { rows } = await this.$http.get('carriers', {
          params: {
            limit: 5,
            query: {
              isActive: true,
              ...(searchStr ? { name: mongoRegexI(searchStr) } : {}),
            },
          },
        });

        this.carrier.list = rows.map((item) => ({ id: item.id, name: item.name, code: item.code }));
      } finally {
        this.carrier.loading = false;
      }
    },
    claimAgentOpen() {
      if (!this.claimAgent.list.length) {
        this.claimAgentSearch();
      }
    },
    async claimAgentSearch(searchStr) {
      this.claimAgent.loading = true;

      try {
        const { rows } = await this.$http.get('claim-agents', {
          params: {
            limit: 5,
            query: {
              isActive: true,
              ...(searchStr ? { name: mongoRegexI(searchStr) } : {}),
            },
          },
        });

        this.claimAgent.list = rows.map((item) => ({ id: item.id, name: item.name, code: item.code }));
      } finally {
        this.claimAgent.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.multiselect__tag--disabled {
  pointer-events: none;
  opacity: 0.6;
}
</style>
